import { createContext, useContext, useState, ReactNode } from 'react';
import { changeUserLang } from '../services/api';

const defaultLang = process.env.REACT_APP_LANG || 'en';

// DEFINE THE SHAPE OF THE CONTEXT DATA
type LanguageContextType = {
    language: string;
    changeLanguage: (newLanguage: string) => void;
    changeUserLanguage: (newLanguage: string, userId: number) => void;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

type LanguageProviderProps = {
    children: ReactNode;
};

export function LanguageProvider({ children }: LanguageProviderProps) {
    //TRY TO GET LANGUAGE FROM LOCAL STORAGE
    let storedLanguage;
    try {
        storedLanguage = localStorage.getItem('language');
    } catch (error) {
        console.error('Failed to get language from local storage', error);
    }
  
    const [language, setLanguage] = useState<string>(storedLanguage || defaultLang); 

    const changeLanguage = (newLanguage: string) => {
        //TRY TO SAVE THE NEW LANGUAGE TO LOCAL STORAGE
        try {
            localStorage.setItem('language', newLanguage);
        } catch (error) {
            console.error('Failed to save language to local storage', error);
        }
        setLanguage(newLanguage);
    };

    const changeUserLanguage = (newLanguage: string, userId: number) => {
        // Send a request to update the language in the database
        changeUserLang(newLanguage) 
            .then(() => {
                // Also update the local storage and state
                try {
                    localStorage.setItem('language', newLanguage);
                } catch (error) {
                    console.error('Failed to save language to local storage', error);
                }
                setLanguage(newLanguage);
            })
            .catch(error => {
                console.error('Failed to update language in the database', error);
            });
    };


    //We annotate the context value provided to the LanguageContext.Provider to ensure it matches the LanguageContextType
    const contextValue: LanguageContextType = {
        language,
        changeLanguage,
        changeUserLanguage,
    };

    return (
        <LanguageContext.Provider value={contextValue}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}